<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
                Annual Revenue
            </h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Annual revenue is the total amount of income your organization generates to support its mission, programs, and operations during a specific period, typically recorded over a fiscal year. Nonprofit organizations depend on funding from a variety of sources; which may include philanthropic contributions and sponsorships, earned revenue, investment income, government appropriations, and in-kind donations of goods, services, or property.</p>
            
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Enter your organization's estimated annual revenue by category over the next three (3) years below.</p>

            <!-- Error Message -->
            <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p class="text-red-600 text-sm">{{ errorMessage }}</p>
            </div>

            <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
                <ActivityIndicator />
            </div>

            <!-- Revenue Table -->
            <div v-else>
                <RevenueTable 
                    :revenue-items="revenueItems" 
                    :locked-revenue-types="['In-Kind Goods/Services']"
                    @data-changed="handleDataChanged"
                    @add-item="handleAddItem"
                    @delete-item="handleDeleteItem"
                />
            </div>
        </div>
    </div>

    <!-- Confirmation Popup -->
    <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p class="text-gray-600 mb-6">Are you sure you want to delete this revenue item?</p>
            <div class="flex justify-end space-x-4">
                <button
                    @click="conformationPopupIsOpen = false"
                    class="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                    Cancel
                </button>
                <button
                    @click="handleConfirmDelete"
                    class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Delete
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import RevenueTable from '@/components/RevenueTable.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useFetchCache } from '@/composables/fetchCache';
import { useFetch } from '@/composables/fetch';
import { usePostAnswer } from '@/composables/postAnswer';
import global from "@/global";
const authStore = useAuthStore();
const isLoading = ref(true);
const errorMessage = ref('');
const isSaving = ref(false);
const conformationPopupIsOpen = ref(false);
const itemToDeleteIndex = ref(null);
const deletionFunction = ref(null);

// Add auto-save debounce timer
let autoSaveTimer = null;
const AUTO_SAVE_DELAY = 2000; // 2 seconds delay

// Define revenue items with types and names
const revenueItems = ref([
    // Philanthropic Contributions
    { name: 'Corporate Gifts', type: 'Philanthropic Contributions', year1: 0, year2: 0, year3: 0 },
    { name: 'Individual Gifts', type: 'Philanthropic Contributions', year1: 0, year2: 0, year3: 0 },
    { name: 'Foundation Grants', type: 'Philanthropic Contributions', year1: 0, year2: 0, year3: 0 },
    { name: 'Government Grants', type: 'Philanthropic Contributions', year1: 0, year2: 0, year3: 0 },
    { name: 'Corporate Sponsorships', type: 'Philanthropic Contributions', year1: 0, year2: 0, year3: 0 },
    // Earned Revenue
    { name: 'Ticket Sales', type: 'Earned Revenue', year1: 0, year2: 0, year3: 0 },
    { name: 'Events', type: 'Earned Revenue', year1: 0, year2: 0, year3: 0 },
    { name: 'Licensing Fees', type: 'Earned Revenue', year1: 0, year2: 0, year3: 0 },
    { name: 'Rentals', type: 'Earned Revenue', year1: 0, year2: 0, year3: 0 },
    { name: 'Tuition', type: 'Earned Revenue', year1: 0, year2: 0, year3: 0 },
    // Investment Income
    { name: 'Endowment Income', type: 'Investment Income', year1: 0, year2: 0, year3: 0 },
    { name: 'Stock/Bonds Dividends', type: 'Investment Income', year1: 0, year2: 0, year3: 0 },
    // Government Appropriations
    { name: 'City', type: 'Government Appropriations', year1: 0, year2: 0, year3: 0 },
    { name: 'State', type: 'Government Appropriations', year1: 0, year2: 0, year3: 0 },
    { name: 'Federal', type: 'Government Appropriations', year1: 0, year2: 0, year3: 0 },
    // In-Kind Goods/Services
    { name: 'Goods/Services', type: 'In-Kind Goods/Services', year1: 0, year2: 0, year3: 0 }
]);

// API Integration using composables
const { error: fetchError, fetchCachedData, cachedData } = useFetchCache({
    uri: "/questions/annual_revenue",
    headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
    },
});

const revenuesFetch = useFetch({
    uri: "/revenues",
    headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
    },
});

const answerPost = usePostAnswer({
    uri: `/revenues`,
    headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
    },
});

// Fetch revenues from API
const fetchRevenues = async () => {
    try {
        // Fetch question data
        await fetchCachedData();
        localStorage.setItem(
            "questionFetchData",
            JSON.stringify(cachedData.value.question)
        );

        // Fetch revenues
        const response = await fetch(`${global.API_URL}/revenues`, {
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        
        // Updated to work with new API structure: { revenues: annual_overhead_revenues }
        if (data.revenues) {
            const revenueData = data.revenues;
            
            // Reset all values to 0 first
            revenueItems.value.forEach(item => {
                item.year1 = 0;
                item.year2 = 0;
                item.year3 = 0;
            });

            // Map API data to revenue items
            const typeMapping = {
                'philanthropic_contributions': 'Philanthropic Contributions',
                'earned_revenue': 'Earned Revenue',
                'investment_income': 'Investment Income',
                'government_appropriations': 'Government Appropriations',
                'in_kind': 'In-Kind Goods/Services',
                'other': 'Other'
            };

            // Process each revenue type
            Object.entries(revenueData).forEach(([apiType, items]) => {
                if (!Array.isArray(items)) return;

                const mappedType = typeMapping[apiType];
                if (!mappedType) return;

                items.forEach(item => {
                    if (!item || !item.revenue_name) return;

                    // For Other type, add items dynamically
                    if (mappedType === 'Other') {
                        const othersItem = {
                            id: item.id,
                            name: item.revenue_name,
                            type: 'Other',
                            year1: parseFloat(item.revenue_cost) || 0,
                            year2: parseFloat(item.revenue_cost_year_2) || 0,
                            year3: parseFloat(item.revenue_cost_year_3) || 0
                        };
                        revenueItems.value.push(othersItem);
                    } else {
                        // Find the matching item in revenueItems for other types
                        const matchingItem = revenueItems.value.find(
                            existingItem => 
                                existingItem.name.toLowerCase() === item.revenue_name.toLowerCase() && 
                                existingItem.type === mappedType
                        );

                        if (matchingItem) {
                            matchingItem.id = item.id;
                            matchingItem.year1 = parseFloat(item.revenue_cost) || 0;
                            matchingItem.year2 = parseFloat(item.revenue_cost_year_2) || 0;
                            matchingItem.year3 = parseFloat(item.revenue_cost_year_3) || 0;
                        } else {
                            console.log('No match found for:', {
                                name: item.revenue_name,
                                type: mappedType
                            }); 
                        }
                    }
                });
            });

        } else {
            console.log('No revenues data in response');
        }
    } catch (error) {
        console.error('Error fetching revenues:', error);
        errorMessage.value = 'Failed to load revenue data. Please try again.';
    } finally {
        isLoading.value = false;
    }
};

// Save revenues to API
const saveRevenues = async () => {
    if (isSaving.value) {
        return;
    }

    try {
        isSaving.value = true;
        
        // Filter out only empty Other items (items with empty names)
        // Save all items including those with zero values
        const revenuesToSave = revenueItems.value
            .filter(item => {
                // For Other type, filter out items with empty names
                if (item.type === 'Other' && (!item.name || item.name.trim() === '')) {
                    return false;
                }
                return true;
            })
            .map(item => ({
                id: item.id,
                revenue_category: 'annual_overhead',
                revenue_type: item.type,
                revenue_name: item.name,
                revenue_cost: item.year1.toString(),
                revenue_cost_year_2: item.year2.toString(),
                revenue_cost_year_3: item.year3.toString()
            }));

        // Don't save if no revenues to save (only if all items are empty Other items)
        if (revenuesToSave.length === 0) {
            return { status: true, revenues: {} };
        }

        const response = await fetch(`${global.API_URL}/revenues`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
            body: JSON.stringify({ 
                revenues: revenuesToSave
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to save revenues');
        }

        const data = await response.json();
        
        // If save was successful and we have new items without IDs, update IDs without reordering
        if (data.status) {
            await updateItemIdsWithoutReordering();
        }
        
        return data;
    } catch (error) {
        console.error('Error saving revenues:', error);
        errorMessage.value = 'Failed to save revenue data. Please try again.';
        throw error;
    } finally {
        isSaving.value = false;
    }
};

// Handle data changes from RevenueTable for auto-save
const handleDataChanged = async (immediate = false) => {
    try {
        // Don't trigger auto-save if we're already saving
        if (isSaving.value) {
            return;
        }
        
        // Clear any existing error message when user makes changes
        if (errorMessage.value) {
            errorMessage.value = '';
        }
        
        // Clear existing timer
        if (autoSaveTimer) {
            clearTimeout(autoSaveTimer);
        }
        
        if (immediate) {
            // Save immediately
            try {
                await saveRevenues();
            } catch (error) {
                console.error('Immediate save failed:', error);
            }
        } else {
            // Set new timer for debounced save
            autoSaveTimer = setTimeout(async () => {
                try {
                    // Trigger auto-save
                    await saveRevenues();
                } catch (error) {
                    console.error('Auto-save failed:', error);
                    // Don't show error to user for auto-save failures to avoid interrupting their workflow
                    // The error will be shown when they try to validate the step
                }
            }, AUTO_SAVE_DELAY);
        }
    } catch (error) {
        console.error('Error setting up auto-save:', error);
    }
};

// Add method to update item IDs without reordering
const updateItemIdsWithoutReordering = async () => {
    try {
        // Check if we have any items without IDs (excluding empty Other items)
        const hasNewItems = revenueItems.value.some(item => {
            // Skip empty Other items
            if (item.type === 'Other' && (!item.name || item.name.trim() === '')) {
                return false;
            }
            return !item.id;
        });
        
        if (!hasNewItems) {
            return; // No new items to update
        }
        
        // Fetch the latest revenues to get new IDs
        const response = await fetch(`${global.API_URL}/revenues`, {
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        
        // Updated to work with new API structure: { revenues: annual_overhead_revenues }
        if (data.revenues) {
            const revenueData = data.revenues;
            
            // Create a map of revenue names to IDs for quick lookup
            const revenueMap = {};
            Object.values(revenueData).flat().forEach(item => {
                if (item && item.revenue_name) {
                    const key = `${item.revenue_type}|${item.revenue_name}`;
                    revenueMap[key] = item.id;
                }
            });
            
            // Update IDs in revenue items without changing order (including items with zero values)
            let updatedCount = 0;
            revenueItems.value.forEach(item => {
                // Skip empty Other items
                if (item.type === 'Other' && (!item.name || item.name.trim() === '')) {
                    return;
                }
                
                if (!item.id) {
                    const key = `${item.type}|${item.name}`;
                    if (revenueMap[key]) {
                        item.id = revenueMap[key];
                        updatedCount++;
                    }
                }
            });
        }
    } catch (error) {
        console.error('Error updating item IDs:', error);
        // Don't throw error to avoid breaking the save flow
    }
};

// Validation function for the step
const validateStep = async () => {
    try {
        // Check if any Other items have empty names (these should be removed)
        const invalidOthersItems = revenueItems.value.filter(item => 
            item.type === 'Other' && 
            (!item.name || item.name.trim() === '')
        );

        if (invalidOthersItems.length > 0) {
            errorMessage.value = 'Please remove "Other" items with empty names, or provide a name for them.';
            return false;
        }

        // Try to save the data (including items with zero values)
        await saveRevenues();
        return true;
    } catch (error) {
        console.error('Validation error:', error);
        return false;
    }
};

// Use the step validation composable
useStepValidation({
    initialValidState: false,
    validateFn: validateStep
});

// Initialize data
onMounted(async () => {
    await fetchRevenues();
});

// Handle adding new item to Other type
const handleAddItem = () => {
    // Add a new item to the Other type
    const newItem = {
        name: '',
        type: 'Other',
        year1: 0,
        year2: 0,
        year3: 0
    };
    revenueItems.value.push(newItem);
    
    // Trigger auto-save
    handleDataChanged();
};

// Delete revenue from API
const deleteRevenue = async (revenueId) => {
    try {
        const response = await fetch(`${global.API_URL}/revenues/${revenueId}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data?.status || false;
    } catch (error) {
        console.error('Error deleting revenue:', error);
        return false;
    }
};

// Handle deleting item from Other type
const handleDeleteItem = (itemIndex) => {
    // Find the item in the revenueItems array
    const item = revenueItems.value[itemIndex];
    if (item && item.type === 'Other') {
        // If item has an ID, show confirmation popup and delete from API
        if (item.id) {
            itemToDeleteIndex.value = itemIndex;
            conformationPopupIsOpen.value = true;
            deletionFunction.value = async () => {
                const success = await deleteRevenue(item.id);
                if (success) {
                    revenueItems.value.splice(itemIndex, 1);
                    // Trigger auto-save to update the UI
                    handleDataChanged();
                } else {
                    console.error('Failed to delete revenue');
                    errorMessage.value = 'Failed to delete revenue item. Please try again.';
                }
            };
        } else {
            // Item doesn't have an ID, just remove from local array
            revenueItems.value.splice(itemIndex, 1);
            // Trigger auto-save
            handleDataChanged();
        }
    }
};

// Handle confirm delete
const handleConfirmDelete = async () => {
    if (deletionFunction.value) {
        await deletionFunction.value();
        conformationPopupIsOpen.value = false;
        deletionFunction.value = null;
        itemToDeleteIndex.value = null;
    }
};

// Cleanup on unmount
onUnmounted(() => {
    if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
    }
});

// Expose saveRevenues to parent component
defineExpose({
    saveRevenues
});
</script>

<style scoped>
</style> 
